import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FiMenu, FiX, FiSearch, FiShoppingCart, FiChevronDown } from 'react-icons/fi';
import { getCartItemCount } from '../lib/cart';
import CartSidebar from './CartSidebar';
import CurrencySelector from './CurrencySelector';
import SearchModal from './SearchModal';

export default function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [cartCount, setCartCount] = useState(0);
  const [activeDropdown, setActiveDropdown] = useState(null);

  useEffect(() => {
    // Update cart count
    const updateCartCount = () => {
      setCartCount(getCartItemCount());
    };

    updateCartCount();

    // Listen for cart changes
    window.addEventListener('cartUpdated', updateCartCount);

    return () => {
      window.removeEventListener('cartUpdated', updateCartCount);
    };
  }, []);

  return (
    <>
      <header className="bg-black/95 backdrop-blur-sm border-b border-gray-800 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Desktop Layout */}
          <div className="hidden md:flex items-center justify-between h-12">
            {/* Logo */}
            <Link to="/" className="flex items-center">
              <img
                src="https://cdnbailebelle.pages.dev/header-logo.png"
                alt="BaileBelle"
                className="h-8 w-auto"
              />
            </Link>

            {/* Desktop Navigation */}
            <nav className="flex items-center space-x-6 text-sm">
              <Link to="/" className="text-white transition-colors" style={{ color: 'white' }} onMouseEnter={(e) => e.target.style.color = '#CF0F63'} onMouseLeave={(e) => e.target.style.color = 'white'}>
                Home
              </Link>

              <Link to="/collections" className="text-white hover:text-pink-500 transition-colors">
                Collections
              </Link>

              {/* Women Dress Dropdown */}
              <div
                className="relative group"
                onMouseEnter={() => setActiveDropdown('women-dress')}
                onMouseLeave={() => setActiveDropdown(null)}
              >
                <div className="flex items-center">
                  <Link
                    to="/collections/dress"
                    className="text-white hover:text-pink-500 transition-colors"
                    onClick={() => setActiveDropdown(null)}
                  >
                    Women Dress
                  </Link>
                  <button
                    className="ml-1 text-white hover:text-pink-500 transition-colors"
                    onClick={() => setActiveDropdown(activeDropdown === 'women-dress' ? null : 'women-dress')}
                  >
                    <FiChevronDown size={16} />
                  </button>
                </div>
                {activeDropdown === 'women-dress' && (
                  <div className="absolute top-full left-0 mt-2 w-48 bg-black border border-gray-800 rounded-lg shadow-lg z-50">
                    <div className="py-2">
                      <Link to="/collections/standard-dress" className="block px-4 py-2 text-white hover:bg-gray-800 hover:text-pink-500 transition-colors">
                        Standard Dress
                      </Link>
                      <Link to="/collections/kids-wear" className="block px-4 py-2 text-white hover:bg-gray-800 hover:text-pink-500 transition-colors">
                        Kids Wear
                      </Link>
                      <Link to="/collections/latin-dress" className="block px-4 py-2 text-white hover:bg-gray-800 hover:text-pink-500 transition-colors">
                        Latin Dress
                      </Link>
                      <Link to="/collections/practice-wear" className="block px-4 py-2 text-white hover:bg-gray-800 hover:text-pink-500 transition-colors">
                        Practice Wear
                      </Link>
                    </div>
                  </div>
                )}
              </div>

              {/* Jewelry Dropdown */}
              <div
                className="relative group"
                onMouseEnter={() => setActiveDropdown('jewelry')}
                onMouseLeave={() => setActiveDropdown(null)}
              >
                <div className="flex items-center">
                  <Link
                    to="/collections/jewelry"
                    className="text-white hover:text-pink-500 transition-colors"
                    onClick={() => setActiveDropdown(null)}
                  >
                    Jewelry
                  </Link>
                  <button
                    className="ml-1 text-white hover:text-pink-500 transition-colors"
                    onClick={() => setActiveDropdown(activeDropdown === 'jewelry' ? null : 'jewelry')}
                  >
                    <FiChevronDown size={16} />
                  </button>
                </div>
                {activeDropdown === 'jewelry' && (
                  <div className="absolute top-full left-0 mt-2 w-48 bg-black border border-gray-800 rounded-lg shadow-lg z-50">
                    <div className="py-2">
                      <Link to="/collections/earrings" className="block px-4 py-2 text-white hover:bg-gray-800 hover:text-pink-500 transition-colors">
                        Earrings
                      </Link>
                      <Link to="/collections/hand-jewelry" className="block px-4 py-2 text-white hover:bg-gray-800 hover:text-pink-500 transition-colors">
                        Hand Jewelry
                      </Link>
                      <Link to="/collections/head-jewelry" className="block px-4 py-2 text-white hover:bg-gray-800 hover:text-pink-500 transition-colors">
                        Head Jewelry
                      </Link>
                      <Link to="/collections/necklace" className="block px-4 py-2 text-white hover:bg-gray-800 hover:text-pink-500 transition-colors">
                        Necklace
                      </Link>
                      <Link to="/collections/other-body-jewelry" className="block px-4 py-2 text-white hover:bg-gray-800 hover:text-pink-500 transition-colors">
                        Other Body Jewelry
                      </Link>
                    </div>
                  </div>
                )}
              </div>

              <Link to="/collections/shoes" className="text-white hover:text-pink-500 transition-colors">
                Women Shoes
              </Link>
              <Link to="/contact" className="text-white hover:text-pink-500 transition-colors">
                Contact Us
              </Link>
            </nav>

            {/* Desktop Actions */}
            <div className="flex items-center space-x-3">
              <CurrencySelector />
              <button
                onClick={() => setIsSearchOpen(true)}
                className="p-1.5 text-white hover:text-pink-500 transition-colors"
                aria-label="Search"
              >
                <FiSearch size={18} />
              </button>

              <button
                onClick={() => setIsCartOpen(true)}
                className="relative p-1.5 text-white hover:text-pink-500 transition-colors"
                aria-label="Shopping Cart"
              >
                <FiShoppingCart size={18} />
                {cartCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-pink-500 text-black text-xs rounded-full h-4 w-4 flex items-center justify-center font-bold text-[10px]">
                    {cartCount}
                  </span>
                )}
              </button>
            </div>
          </div>

          {/* Mobile Layout - Logo centered, actions on left and right */}
          <div className="md:hidden flex items-center justify-between h-12 relative px-1">
            {/* Left side - Menu button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="p-2 text-white hover:text-pink-500 transition-colors flex-shrink-0 z-10"
              aria-label="Toggle menu"
            >
              {isMenuOpen ? <FiX size={20} /> : <FiMenu size={20} />}
            </button>

            {/* Center - Logo */}
            <Link to="/" className="absolute left-1/2 transform -translate-x-1/2 flex items-center z-10">
              <img
                src="https://cdnbailebelle.pages.dev/header-logo.png"
                alt="BaileBelle"
                className="h-7 sm:h-8 w-auto max-w-[120px] sm:max-w-none"
              />
            </Link>

            {/* Right side - Currency, Search and Cart */}
            <div className="flex items-center space-x-0.5 sm:space-x-1 flex-shrink-0 z-10">
              <div className="mr-0.5 sm:mr-1">
                <CurrencySelector />
              </div>
              <button
                onClick={() => setIsSearchOpen(true)}
                className="p-1.5 text-white hover:text-pink-500 transition-colors"
                aria-label="Search"
              >
                <FiSearch size={18} />
              </button>

              <button
                onClick={() => setIsCartOpen(true)}
                className="relative p-1.5 text-white hover:text-pink-500 transition-colors"
                aria-label="Shopping Cart"
              >
                <FiShoppingCart size={18} />
                {cartCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-pink-500 text-black text-xs rounded-full h-4 w-4 flex items-center justify-center font-bold text-[10px]">
                    {cartCount}
                  </span>
                )}
              </button>
            </div>
          </div>

          {/* Mobile Navigation */}
          {isMenuOpen && (
            <div className="md:hidden border-t border-gray-800 py-3">
              <nav className="flex flex-col space-y-3 text-sm">
                <Link
                  to="/"
                  className="text-white hover:text-pink-500 transition-colors"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Home
                </Link>

                <Link
                  to="/collections"
                  className="text-white hover:text-pink-500 transition-colors"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Collections
                </Link>

                {/* Women Dress Mobile Submenu */}
                <div className="space-y-2">
                  <Link
                    to="/collections/dress"
                    className="text-white font-medium hover:text-pink-500 transition-colors"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Women Dress
                  </Link>
                  <div className="ml-4 space-y-2">
                    <Link
                      to="/collections/standard-dress"
                      className="block text-gray-300 hover:text-pink-500 transition-colors"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      Standard Dress
                    </Link>
                    <Link
                      to="/collections/kids-wear"
                      className="block text-gray-300 hover:text-pink-500 transition-colors"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      Kids Wear
                    </Link>
                    <Link
                      to="/collections/latin-dress"
                      className="block text-gray-300 hover:text-pink-500 transition-colors"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      Latin Dress
                    </Link>
                    <Link
                      to="/collections/practice-wear"
                      className="block text-gray-300 hover:text-pink-500 transition-colors"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      Practice Wear
                    </Link>
                  </div>
                </div>

                {/* Jewelry Mobile Submenu */}
                <div className="space-y-2">
                  <Link
                    to="/collections/jewelry"
                    className="text-white font-medium hover:text-pink-500 transition-colors"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Jewelry
                  </Link>
                  <div className="ml-4 space-y-2">
                    <Link
                      to="/collections/earrings"
                      className="block text-gray-300 hover:text-pink-500 transition-colors"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      Earrings
                    </Link>
                    <Link
                      to="/collections/hand-jewelry"
                      className="block text-gray-300 hover:text-pink-500 transition-colors"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      Hand Jewelry
                    </Link>
                    <Link
                      to="/collections/head-jewelry"
                      className="block text-gray-300 hover:text-pink-500 transition-colors"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      Head Jewelry
                    </Link>
                    <Link
                      to="/collections/necklace"
                      className="block text-gray-300 hover:text-pink-500 transition-colors"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      Necklace
                    </Link>
                    <Link
                      to="/collections/other-body-jewelry"
                      className="block text-gray-300 hover:text-pink-500 transition-colors"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      Other Body Jewelry
                    </Link>
                  </div>
                </div>

                <Link
                  to="/collections/women-shoes"
                  className="text-white hover:text-pink-500 transition-colors"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Women Shoes
                </Link>
                <Link
                  to="/contact"
                  className="text-white hover:text-pink-500 transition-colors"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Contact Us
                </Link>
                <Link
                  to="/about"
                  className="text-white hover:text-pink-500 transition-colors"
                  onClick={() => setIsMenuOpen(false)}
                >
                  About Us
                </Link>
              </nav>
            </div>
          )}
        </div>
      </header>

      {/* Modals */}
      <CartSidebar isOpen={isCartOpen} onClose={() => setIsCartOpen(false)} />
      <SearchModal isOpen={isSearchOpen} onClose={() => setIsSearchOpen(false)} />
    </>
  );
}
